import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { fetchManagedOfferContent as fetchManagedOfferQuery, fetchManagedUpgradeContent as fetchManagedUpgradeQuery } from '@/queries'
import { useLazyQuery } from '@vue/apollo-composable'
import { sentryCapture } from '@/utils/sentry'
import getAssetProxy from '@/utils/getAssetProxy'
import { DocumentNode } from '@apollo/client'

export const useContentStore = defineStore('ContentStore', () => {
  const content = ref()

  const { onResult: onFetchContentResult, load: fetchManagedContent, loading, onError: onFetchContentError } = useLazyQuery(fetchManagedUpgradeQuery)

  const reduceContent = (managedStoryResponse: any) => {
    if (!managedStoryResponse?.story) {
      return
    }

    const { content } = managedStoryResponse.story
    return content
  }

  const fetchContent = async (query: DocumentNode, path: string) => {
    const ROOT_PATH = 'application-resources/subscriptions/'
    const version = import.meta.env.VITE_OUTPUT_ENVIRONMENT !== 'production' ? 'draft' : 'published'
    const storyId = `${ROOT_PATH}${path}`

    return fetchManagedContent(
      query,
      {
        storyId,
        version
      },
      {
        errorPolicy: 'ignore',
        fetchPolicy: 'cache-first',
        context: {
          fetchOptions: {
            method: 'GET'
          }
        }
      }
    )
  }

  const fetchOfferContent = (path: string) => fetchContent(fetchManagedOfferQuery, path)
  const fetchUpgradeContent = (path: string) => fetchContent(fetchManagedUpgradeQuery, path)

  onFetchContentResult(result => {
    if (!result.data) {
      return
    }

    const { fetchManagedContent } = result.data
    content.value = reduceContent(fetchManagedContent)
  })

  onFetchContentError(err => sentryCapture(err))

  const contentSections = computed(() => content.value?.contentSections)
  const ctaText = computed(() => content.value?.ctaText)
  const headerTitle = computed(() => content.value?.headerTitle)

  const headerImage = computed(() => {
    const image = content.value?.headerImage
    return image?.filename ? getAssetProxy(image.filename) : ''
  })

  return {
    content,
    contentSections,
    ctaText,
    fetchOfferContent,
    fetchUpgradeContent,
    headerImage,
    headerTitle,
    loading
  }
})
